import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime, humanize } from '@/lib/format';
import type { Message } from '@/types/platform';

export interface WebhookEvent {
  id: string;
  provider: string;
  eventType: string;
  status: string;
  createdAt: string;
  processedAt: string | null;
}

interface LogsTabProps {
  messages: Message[];
  webhookEvents: WebhookEvent[];
}

const MESSAGE_TONES: Record<Message['status'], Tone> = {
  queued: 'neutral',
  sent: 'success',
  simulated: 'warning',
  failed: 'danger',
  blocked: 'danger',
};

const LogsTab = ({ messages, webhookEvents }: LogsTabProps) => (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Mail log</h2>
        </CardTitle>
        <CardDescription>The most recent mail this app sent, newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {messages.length === 0 ? (
          <EmptyState description="This app has not sent any mail." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>To</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.id}>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(message.createdAt)}
                  </TableCell>
                  <TableCell>{message.kind}</TableCell>
                  <TableCell>{message.toAddress}</TableCell>
                  <TableCell>{message.fromAddress ?? '—'}</TableCell>
                  <TableCell>{message.subject}</TableCell>
                  <TableCell className="whitespace-normal">
                    <StatusPill tone={MESSAGE_TONES[message.status]}>
                      {humanize(message.status)}
                    </StatusPill>
                    {message.error ? (
                      <p className="mt-1 max-w-xs min-w-40 text-xs break-words text-red-700">
                        {message.error}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(message.sentAt) || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Webhook log</h2>
        </CardTitle>
        <CardDescription>
          The most recent events providers sent about this app, newest first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {webhookEvents.length === 0 ? (
          <EmptyState description="No webhook events have arrived for this app." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhookEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(event.createdAt)}
                  </TableCell>
                  <TableCell>{event.provider}</TableCell>
                  <TableCell>
                    <code className="text-xs">{event.eventType}</code>
                  </TableCell>
                  <TableCell>{humanize(event.status)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(event.processedAt) || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  </div>
);

export default LogsTab;
